// ChartComponents.js - Calendar Heatmap Component
import React from 'react';
import styles from './Insights.module.css';

// HeatMap Component (Calendar grid)
export function HeatMap({ data, title, height = 250 }) {
  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className={styles.heatmapContainer} style={{ height: `${height}px` }}>
        <div className={styles.heatmapEmpty}>
          <p>No heatmap data available yet</p>
          <p>Start tracking daily to see your activity pattern</p>
        </div>
      </div>
    );
  }

  // Group data by weeks (each week has 7 days)
  const groupByWeeks = (heatmapData) => {
    const weeks = [];
    let currentWeek = [];
    
    heatmapData.forEach((day, index) => {
      currentWeek.push(day);
      
      // Start new week after 7 days or at the end
      if (currentWeek.length === 7 || index === heatmapData.length - 1) {
        weeks.push([...currentWeek]);
        currentWeek = [];
      }
    });
    
    return weeks;
  };

  const weeks = groupByWeeks(data.data);
  
  // Day labels for the top
  const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
  // Get color based on intensity
  const getColor = (intensity) => {
    switch(intensity) {
      case 0: return '#196127';  // Dark green - clean
      case 1: return '#c6e48b';  // Light green - minimal
      case 2: return '#7bc96f';  // Medium green - moderate
      case 3: return '#239a3b';  // Bright green - high
      default: return '#ebedf0'; // Gray - no data
    }
  };
  
  // Get tooltip text
  const getTooltip = (day) => {
    if (!day) return '';
    
    const date = new Date(day.date);
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
    
    const faps = day.value || 0;
    
    if (day.intensity === 0) {
      return `${dateStr}: Clean day ✓ (0 faps)`;
    } else if (day.intensity === 1) {
      return `${dateStr}: Minimal activity (${faps} fap${faps !== 1 ? 's' : ''})`;
    } else if (day.intensity === 2) {
      return `${dateStr}: Moderate activity (${faps} faps)`;
    } else if (day.intensity === 3) {
      return `${dateStr}: High activity (${faps} faps)`;
    }
    
    return `${dateStr}: No data`;
  };

  return (
    <div className={styles.heatmapContainer} style={{ minHeight: `${height}px` }}>
      {title && <h3 className={styles.heatmapTitle}>{title}</h3>}
      
      <div className={styles.calendarHeatmap}>
        {/* Day labels */}
        <div className={styles.dayLabels}>
          <div className={styles.dayLabel}></div> {/* Empty cell for week numbers */}
          {dayLabels.map((day, index) => (
            <div key={index} className={styles.dayLabel}>{day}</div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className={styles.weeksContainer}>
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className={styles.weekRow}>
              {/* Week number */}
              <div className={styles.weekNumber}>
                {weekIndex + 1}
              </div>
              
              {/* Days in week */}
              {week.map((day, dayIndex) => (
                <div
                  key={`${weekIndex}-${dayIndex}`}
                  className={styles.dayCell}
                  style={{ 
                    backgroundColor: day ? getColor(day.intensity) : '#ebedf0',
                    border: day?.is_today ? '2px solid #ff6b6b' : '1px solid rgba(255, 255, 255, 0.1)',
                    opacity: day ? 1 : 0.5
                  }}
                  title={getTooltip(day)}
                  data-intensity={day?.intensity || -1}
                >
                  {/* Show day number for first week, today, or high intensity days */}
                  {(weekIndex === 0 || day?.is_today || day?.intensity === 3) && day && (
                    <span className={styles.dayNumber}>
                      {new Date(day.date).getDate()}
                    </span>
                  )}
                </div>
              ))}
              
              {/* Fill empty cells if week is incomplete */}
              {Array(7 - week.length).fill().map((_, index) => (
                <div 
                  key={`empty-${index}`}
                  className={styles.dayCell}
                  style={{ 
                    backgroundColor: '#ebedf0',
                    opacity: 0.3
                  }}
                  title="No data"
                />
              ))}
            </div>
          ))}
        </div>
        
        {/* Month labels */}
        <div className={styles.monthLabels}>
          <span>{weeks.length > 0 ? 
            new Date(weeks[0][0]?.date).toLocaleDateString('en-US', { month: 'short' }) : 
            'Start'
          }</span>
          <span>• • •</span>
          <span>Today</span>
        </div>
      </div>
    </div>
  );
}

export default { HeatMap };
